import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { applyThemeToDom } from "@/context/ThemeContext";
import { Sparkles, Star } from "lucide-react";

// Public share page — NO auth, NO cookies, NO sign-up nag.
// Uses a bare axios instance (no interceptors) to avoid attaching any auth headers.
const bareAxios = axios.create({ baseURL: API_BASE });

export default function PublicShare() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await bareAxios.get(`/public/shares/${token}`);
        setPayload(data);
        if (data.theme) applyThemeToDom(data.theme);
      } catch (e) {
        setError(e?.response?.data?.detail || "This link isn't available.");
      }
    })();
  }, [token]);

  if (error) return <NotFound message={error} />;
  if (!payload) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="font-hand text-xl text-muted">a moment…</div>
    </div>
  );

  const hasFields = new Set(payload.fields || []);

  return (
    <div className="min-h-screen relative">
      <div className="ascend-atmosphere" />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Header */}
        <div className="fade-up">
          <div className="flex items-center gap-2 mb-2">
            <Star size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
            <span className="eyebrow">{payload.display_name}'s week</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl leading-tight" style={{ color: "var(--text)" }} data-testid="public-title">
            {formatRange(payload.week_start_date, payload.week_end_date)}
          </h1>
          {payload.north_star_short && (
            <blockquote className="mt-4 italic font-display text-lg sm:text-2xl leading-snug" style={{ color: "var(--text-muted)" }} data-testid="public-northstar">
              "{payload.north_star_short}"
            </blockquote>
          )}
        </div>

        {/* Personal note */}
        {payload.personal_note && (
          <div className="mt-8 rounded-3xl p-5 sm:p-6 fade-up delay-1" style={{ background: "color-mix(in oklab, var(--primary-soft) 40%, var(--surface))", border: "1px solid var(--border)" }} data-testid="public-note">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
              <span className="eyebrow">a note</span>
            </div>
            <p className="font-display italic text-lg sm:text-xl leading-snug" style={{ color: "var(--text)" }}>
              "{payload.personal_note}"
            </p>
          </div>
        )}

        {/* Stats strip */}
        {(hasFields.has("blocks") || hasFields.has("streak") || hasFields.has("mood_emoji") || hasFields.has("mood_avg")) && (
          <div className="mt-8 grid grid-cols-3 gap-3 fade-up delay-2">
            {hasFields.has("blocks") && (
              <StatCard label="blocks done" value={payload.blocks_completed ?? 0} testid="public-blocks" />
            )}
            {hasFields.has("streak") && (
              <StatCard label="streak" value={`${payload.streak_days ?? 0}d`} testid="public-streak" />
            )}
            {hasFields.has("mood_avg") && (
              <StatCard label="mood" value={payload.mood_emoji || "—"} testid="public-mood" />
            )}
          </div>
        )}

        {/* Hours by subject */}
        {hasFields.has("hours") && payload.hours?.subjects?.length > 0 && (
          <section className="mt-8 card-soft p-5 sm:p-6 fade-up delay-3" data-testid="public-hours">
            <span className="eyebrow">hours by subject</span>
            <div className="mt-3 space-y-3">
              {payload.hours.subjects.map((s, i) => {
                const targetMin = (s.target_hours || 0) * 60;
                const pct = targetMin > 0 ? Math.min(100, Math.round((s.minutes / targetMin) * 100)) : 0;
                const hours = (s.minutes / 60).toFixed(2).replace(/\.?0+$/, "");
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                        <span style={{ color: "var(--text)" }}>{s.name}</span>
                      </span>
                      <span className="text-muted text-xs">{hours}h{s.target_hours ? ` / ${s.target_hours}h` : ""}</span>
                    </div>
                    <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%`, background: s.color }} /></div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* EPQ milestones */}
        {hasFields.has("epq_milestones") && payload.epq_milestones?.length > 0 && (
          <section className="mt-6 card-soft p-5 sm:p-6 fade-up delay-4" data-testid="public-epq">
            <span className="eyebrow">EPQ milestones</span>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {payload.epq_milestones.map((m) => (
                <div key={m.milestone_name} className="rounded-xl px-2 py-2 text-center text-xs font-medium" style={{
                  background: m.status === "done" ? "color-mix(in oklab, var(--primary) 25%, var(--surface))" : "var(--surface-2)",
                  color: m.status === "done" ? "var(--primary)" : "var(--text-muted)",
                  border: "1px solid var(--border)",
                }}>
                  <div className="text-[10px] uppercase tracking-wider">{m.status.replace("_", " ")}</div>
                  <div className="font-display text-sm mt-0.5" style={{ color: m.status === "done" ? "var(--primary)" : "var(--text)" }}>{cap(m.milestone_name)}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Wins */}
        {hasFields.has("wins") && payload.wins?.length > 0 && (
          <section className="mt-6 card-soft p-5 sm:p-6 fade-up" data-testid="public-wins">
            <span className="eyebrow">wins this week</span>
            <ul className="mt-3 space-y-3">
              {payload.wins.map((w) => (
                <li key={w.date}>
                  <div className="text-xs text-muted">{w.date}</div>
                  <div className="font-display italic text-lg leading-snug mt-0.5" style={{ color: "var(--text)" }}>"{w.text}"</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="mt-14 text-center">
          <div className="font-hand text-base" style={{ color: "var(--text-muted)" }}>Made in Ascend.</div>
        </footer>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl px-3 py-4 text-center card-soft-2" data-testid={testid}>
      <div className="font-display text-3xl leading-none" style={{ color: "var(--text)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{label}</div>
    </div>
  );
}

function NotFound({ message }) {
  return (
    <div className="min-h-screen relative flex items-center justify-center px-6">
      <div className="ascend-atmosphere" />
      <div className="max-w-md w-full text-center fade-up">
        <div className="font-display text-5xl mb-2" style={{ color: "var(--text)" }}>Not here.</div>
        <p className="text-muted mb-6">{message}</p>
        <Link to="/" className="btn-ghost inline-flex text-sm">← Ascend home</Link>
      </div>
    </div>
  );
}

function formatRange(a, b) {
  try {
    const [ya, ma, da] = a.split("-").map(Number);
    const [yb, mb, db] = b.split("-").map(Number);
    const optsShort = { day: "numeric", month: "short" };
    const optsLong = { day: "numeric", month: "short", year: "numeric" };
    const da2 = new Date(ya, ma - 1, da).toLocaleDateString("en-GB", optsShort);
    const db2 = new Date(yb, mb - 1, db).toLocaleDateString("en-GB", ya === yb ? optsLong : optsLong);
    return `${da2} — ${db2}`;
  } catch { return `${a} — ${b}`; }
}
function cap(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }
